/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { SystemScope } from '@opengeoweb/shared';
import ViewPresetsFormDialog from './ViewPresetsFormDialog';
import { useDeleteViewPreset } from '../../api/viewpreset-api/hooks';
import type { AppStore } from '../../store/types';
import {
  viewPresetActions,
  viewPresetSelectors,
} from '../../store/viewPresets';
import { PresetAction } from '../../store/viewPresets/types';
import { getViewComponentType } from '../../store/workspace/selectors';
import type { WorkspacePresetLabels } from '../../store/workspace/types';

const ViewPresetsFormDialogConnect: React.FC<{
  scope?: SystemScope;
}> = ({ scope = 'user' }) => {
  const dispatch = useDispatch();
  const dialogOptions = useSelector((store: AppStore) =>
    viewPresetSelectors.getDialogOptions(store),
  );
  const panelId = dialogOptions?.panelId!;
  const activeViewPresetId = useSelector((store: AppStore) =>
    viewPresetSelectors.getViewPresetActiveId(store, panelId),
  );
  const fallbackComponentType = useSelector((store: AppStore) =>
    getViewComponentType(store, panelId),
  );
  const viewComponentType =
    dialogOptions?.formValues.componentType ?? fallbackComponentType;
  const { mutateAsync: deleteViewPreset } = useDeleteViewPreset();

  const onReset = React.useCallback(
    (viewPresetId: string) => {
      dispatch(
        viewPresetActions.selectViewPreset({
          panelId,
          viewPresetId,
          shouldReset: true,
        }),
      );
    },
    [dispatch, panelId],
  );

  const closeDialog = React.useCallback(() => {
    dispatch(viewPresetActions.closeViewPresetDialog());
  }, [dispatch]);

  const onClose = (): void => {
    closeDialog();
  };

  const onSuccess = (
    action: PresetAction,
    newViewPresetId: string,
    title: string,
    description: string,
    abstract: string,
    labels: WorkspacePresetLabels,
  ): void => {
    dispatch(
      viewPresetActions.onSuccessViewPresetAction({
        panelId,
        viewPresetId: newViewPresetId,
        activeViewPresetId,
        shouldSetActiveViewPreset: dialogOptions?.shouldSetActiveViewPreset,
        action,
        title,
        description,
        scope,
        abstract,
        labels,
      }),
    );
  };

  const isOpen = dialogOptions !== undefined;

  return (
    <ViewPresetsFormDialog
      action={dialogOptions?.action || PresetAction.SAVE}
      formValues={dialogOptions?.formValues}
      isOpen={isOpen}
      onClose={onClose}
      onDelete={deleteViewPreset}
      onReset={onReset}
      onSuccess={onSuccess}
      title={dialogOptions?.title || ''}
      viewComponentType={viewComponentType}
      viewPresetId={dialogOptions?.viewPresetId || ''}
    />
  );
};

export default ViewPresetsFormDialogConnect;
